import { createContext, PropsWithChildren, useCallback, useContext, useEffect } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { hasRole } from "../lib/auth";
import { setFeatureUser } from "../lib/delivery";
import { getOwnProfile } from "../lib/expert_insights_api";
import type { ContributorProfile } from "../lib/expert_insights_api";
import { useLocalStorageState } from "../hooks/useLocalStorageState";
import { useState } from "react";

export interface UserState {
  session: Session | null;
  loading: boolean;
  /** Expert profile — non-null only for expert_insights.expert users. Backed by localStorage so the avatar doesn't blink across layout mounts. */
  profile: ContributorProfile | null;
  /** Call after updating the profile to sync the cached value. */
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const UserContext = createContext<UserState | null>(null);

export function UserProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const userId = session?.user?.id ?? null;
  const email = session?.user?.email;
  const cacheKey = userId ? `rc_profile_${userId}` : null;
  const [profile, setProfile] = useLocalStorageState<ContributorProfile>(cacheKey);

  const fetchProfile = useCallback(
    async (sess: Session | null) => {
      const user = sess?.user ?? null;
      if (!user || !hasRole(user, "expert_insights.expert")) {
        setProfile(null);
        return;
      }
      // Cached value is already in state via useLocalStorageState; fetch fresh and update
      try {
        const fresh = await getOwnProfile();
        setProfile(fresh);
      } catch {
        // Keep cached value if available; silently ignore otherwise
      }
    },
    [setProfile]
  );

  const refreshProfile = useCallback(async () => {
    await fetchProfile(session);
  }, [fetchProfile, session]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
      void fetchProfile(data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      void fetchProfile(sess);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  // Keep the Statsig user in sync with the session so flags, dynamic configs, and metric events
  // are attributed to the signed-in user (falling back to anonymous when logged out).
  useEffect(() => {
    setFeatureUser(userId ? { userId, email } : undefined);
  }, [userId, email]);

  async function signOut() {
    setProfile(null); // also removes from localStorage via useLocalStorageState
    await supabase.auth.signOut();
  }

  return (
    <UserContext.Provider value={{ session, loading, profile, refreshProfile, signOut }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext(): UserState {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUserContext must be used inside UserProvider");
  return ctx;
}
